import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Mail, Inbox, CheckCircle2, Clock, AlertTriangle,
  ChevronRight, Send, X, RefreshCw, Search,
  ArrowLeft, User, Tag, Flag, MoreHorizontal,
} from "lucide-react";
import { SectionLabel, FilterChip } from "./atoms";

/* ============================================================
   DNH Admin — Support Desk (Resend-powered inbound + outbound)
   ============================================================ */

const BASE = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── Status/Category/Priority configs ──────────────────────────

const STATUS_CFG = {
  open:        { label: "Open",        dot: "bg-[var(--crimson)]", text: "text-[var(--crimson)]" },
  in_progress: { label: "In Progress", dot: "bg-amber-500",        text: "text-amber-600" },
  closed:      { label: "Closed",      dot: "bg-emerald-500",      text: "text-emerald-600" },
  spam:        { label: "Spam",        dot: "bg-[var(--muted)]",   text: "text-[var(--muted)]" },
};

const CAT_CFG = {
  contact:   { label: "Contact",   bg: "bg-blue-50 border-blue-200 text-blue-700" },
  complaint: { label: "Complaint", bg: "bg-orange-50 border-orange-200 text-orange-700" },
  support:   { label: "Support",   bg: "bg-purple-50 border-purple-200 text-purple-700" },
  refund:    { label: "Refund",    bg: "bg-red-50 border-red-200 text-red-700" },
  general:   { label: "General",   bg: "bg-[var(--surface-2)] border-[var(--hairline)] text-[var(--ink-2)]" },
};

const PRI_CFG = {
  urgent: { label: "Urgent", color: "text-[var(--crimson)]" },
  high:   { label: "High",   color: "text-amber-600" },
  normal: { label: "Normal", color: "text-[var(--muted)]" },
  low:    { label: "Low",    color: "text-[var(--muted)]" },
};

const StatusBadge = ({ status }) => {
  const c = STATUS_CFG[status] || STATUS_CFG.open;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      <span className={`font-mono text-[10.5px] uppercase tracking-[0.1em] ${c.text}`}>{c.label}</span>
    </span>
  );
};

const CatBadge = ({ cat }) => {
  const c = CAT_CFG[cat] || CAT_CFG.general;
  return (
    <span className={`inline-flex items-center h-5 px-2 rounded-[4px] border font-mono text-[10px] uppercase tracking-[0.06em] ${c.bg}`}>
      {c.label}
    </span>
  );
};

// ── Stats Row ──────────────────────────────────────────────────

const StatsRow = ({ stats }) => {
  const cards = [
    { label: "Open",        value: stats?.open ?? "—",        icon: <Inbox size={15} />,        accent: "text-[var(--crimson)]" },
    { label: "In Progress", value: stats?.in_progress ?? "—", icon: <Clock size={15} />,        accent: "text-amber-600" },
    { label: "Closed",      value: stats?.closed ?? "—",      icon: <CheckCircle2 size={15} />, accent: "text-emerald-600" },
    { label: "Today",       value: stats?.today ?? "—",       icon: <Mail size={15} />,         accent: "text-[var(--ink)]" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="support-stats-row">
      {cards.map((c) => (
        <div key={c.label} className="bg-[var(--surface-2)] border border-[var(--hairline)] rounded-[6px] px-4 py-3">
          <div className={`flex items-center gap-1.5 ${c.accent} mb-1`}>{c.icon}
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--muted)]">{c.label}</span>
          </div>
          <div className="font-serif text-[28px] leading-none text-[var(--ink)]">{c.value}</div>
        </div>
      ))}
    </div>
  );
};

// ── Ticket Row ─────────────────────────────────────────────────

const TicketRow = ({ ticket, isSelected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`ticket-row-${ticket.ticket_id}`}
    className={`w-full text-left px-4 py-3.5 border-b border-[var(--hairline)] transition-colors ${
      isSelected
        ? "bg-[var(--crimson)]/5 border-l-2 border-l-[var(--crimson)]"
        : "hover:bg-[var(--surface-2)] border-l-2 border-l-transparent"
    }`}
  >
    <div className="flex items-start justify-between gap-2 mb-1">
      <span className="font-mono text-[11px] text-[var(--muted)] tracking-[0.06em]">{ticket.ticket_id}</span>
      <span className="font-mono text-[10px] text-[var(--muted)] shrink-0">{timeAgo(ticket.last_message_at)}</span>
    </div>
    <div className="font-serif text-[14px] text-[var(--ink)] leading-snug mb-1.5 line-clamp-1">{ticket.subject}</div>
    <div className="flex items-center gap-2 flex-wrap">
      <StatusBadge status={ticket.status} />
      <CatBadge cat={ticket.category} />
      <span className="font-mono text-[10px] text-[var(--muted)] truncate max-w-[120px]">{ticket.from_email}</span>
      {ticket.message_count > 1 && (
        <span className="font-mono text-[10px] text-[var(--muted)]">{ticket.message_count} msgs</span>
      )}
    </div>
  </button>
);

// ── Message Bubble ─────────────────────────────────────────────

const MessageBubble = ({ msg }) => {
  const isOut = msg.direction === "outbound";
  return (
    <div className={`flex flex-col mb-5 ${isOut ? "items-end" : "items-start"}`} data-testid={`msg-bubble-${msg.message_id}`}>
      <div className="flex items-center gap-2 mb-1.5">
        {!isOut && <span className="font-mono text-[10px] text-[var(--muted)] uppercase tracking-[0.08em]">{msg.from_name || msg.from_email}</span>}
        {isOut && <span className="font-mono text-[10px] text-[var(--crimson)] uppercase tracking-[0.08em]">DNH Support</span>}
        <span className="font-mono text-[9.5px] text-[var(--muted)]">{formatDate(msg.sent_at)}</span>
      </div>
      <div
        className={`max-w-[85%] rounded-[8px] px-4 py-3 text-[13.5px] leading-relaxed ${
          isOut
            ? "bg-[var(--ink)] text-[var(--paper)]"
            : "bg-[var(--surface-2)] border border-[var(--hairline)] text-[var(--ink)]"
        }`}
        style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
      >{msg.body_text || (msg.body_html || "").replace(/<[^>]*>/g, " ")}</div>
    </div>
  );
};

// ── Ticket Detail Panel ────────────────────────────────────────

const TicketDetail = ({ ticketId, onBack, onRefreshList }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [sendOk, setSendOk] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/api/admin/support/tickets/${ticketId}`, { credentials: "include" });
      if (r.ok) setData(await r.json());
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const handleStatusChange = async (newStatus) => {
    await fetch(`${BASE}/api/admin/support/tickets/${ticketId}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: newStatus }),
    });
    load();
    onRefreshList();
  };

  const handlePriorityChange = async (newPriority) => {
    await fetch(`${BASE}/api/admin/support/tickets/${ticketId}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ priority: newPriority }),
    });
    load();
  };

  const handleSend = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    setSendError("");
    setSendOk(false);
    try {
      const r = await fetch(`${BASE}/api/admin/support/tickets/${ticketId}/reply`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: replyText }),
      });
      if (r.ok) {
        setReplyText("");
        setSendOk(true);
        setTimeout(() => setSendOk(false), 3000);
        load();
        onRefreshList();
      } else {
        const err = await r.json();
        setSendError(err.detail || "Send failed");
      }
    } catch {
      setSendError("Network error");
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div className="flex-1 flex items-center justify-center text-[var(--muted)] font-mono text-[12px]" data-testid="ticket-detail-loading">
      Loading thread…
    </div>
  );

  if (!data) return (
    <div className="flex-1 flex items-center justify-center text-[var(--muted)]" data-testid="ticket-detail-empty">
      Ticket not found.
    </div>
  );

  const { ticket, messages } = data;
  const priCfg = PRI_CFG[ticket.priority] || PRI_CFG.normal;

  return (
    <div className="flex flex-col h-full" data-testid="ticket-detail-panel">
      {/* Header */}
      <div className="border-b border-[var(--hairline)] px-5 py-4 shrink-0">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={onBack}
            className="md:hidden p-1 -ml-1 text-[var(--muted)] hover:text-[var(--ink)]"
            data-testid="ticket-back-btn"
          >
            <ArrowLeft size={16} />
          </button>
          <span className="font-mono text-[13px] font-bold tracking-[0.06em] text-[var(--ink)]" data-testid="ticket-detail-id">
            {ticket.ticket_id}
          </span>
          <StatusBadge status={ticket.status} />
          <CatBadge cat={ticket.category} />
          <span className={`font-mono text-[10.5px] uppercase tracking-[0.08em] ${priCfg.color}`}>
            {priCfg.label}
          </span>
        </div>
        <h3 className="font-serif text-[16px] leading-snug text-[var(--ink)] mb-1" data-testid="ticket-detail-subject">
          {ticket.subject}
        </h3>
        <div className="flex items-center gap-3 text-[var(--muted)] font-mono text-[10.5px]">
          <User size={11} />
          <span>{ticket.from_name}</span>
          <span className="text-[var(--hairline-strong)]">·</span>
          <a href={`mailto:${ticket.from_email}`} className="hover:text-[var(--crimson)] transition-colors">
            {ticket.from_email}
          </a>
          <span className="text-[var(--hairline-strong)]">·</span>
          <span>{formatDate(ticket.created_at)}</span>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {ticket.status !== "in_progress" && (
            <button
              onClick={() => handleStatusChange("in_progress")}
              data-testid="ticket-action-inprogress"
              className="inline-flex items-center h-7 px-3 rounded-full border border-amber-300 text-amber-700 bg-amber-50 font-mono text-[10px] uppercase tracking-[0.08em] hover:bg-amber-100 transition-colors"
            >
              Mark In Progress
            </button>
          )}
          {ticket.status !== "closed" && (
            <button
              onClick={() => handleStatusChange("closed")}
              data-testid="ticket-action-close"
              className="inline-flex items-center h-7 px-3 rounded-full border border-emerald-300 text-emerald-700 bg-emerald-50 font-mono text-[10px] uppercase tracking-[0.08em] hover:bg-emerald-100 transition-colors"
            >
              <CheckCircle2 size={11} className="mr-1" /> Close Ticket
            </button>
          )}
          {ticket.status !== "open" && (
            <button
              onClick={() => handleStatusChange("open")}
              data-testid="ticket-action-reopen"
              className="inline-flex items-center h-7 px-3 rounded-full border border-[var(--hairline-strong)] text-[var(--ink-2)] font-mono text-[10px] uppercase tracking-[0.08em] hover:border-[var(--crimson)] hover:text-[var(--crimson)] transition-colors"
            >
              Re-open
            </button>
          )}
          {ticket.status !== "spam" && (
            <button
              onClick={() => handleStatusChange("spam")}
              data-testid="ticket-action-spam"
              className="inline-flex items-center h-7 px-3 rounded-full border border-[var(--hairline)] text-[var(--muted)] font-mono text-[10px] uppercase tracking-[0.08em] hover:border-[var(--crimson)] hover:text-[var(--crimson)] transition-colors"
            >
              Mark Spam
            </button>
          )}
          {/* Priority selector */}
          <select
            value={ticket.priority}
            onChange={(e) => handlePriorityChange(e.target.value)}
            data-testid="ticket-priority-select"
            className="h-7 px-2 rounded-full border border-[var(--hairline)] bg-transparent font-mono text-[10px] uppercase tracking-[0.06em] text-[var(--ink-2)] cursor-pointer"
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
      </div>

      {/* Messages thread */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1" data-testid="ticket-thread">
        {messages.length === 0 ? (
          <p className="text-[var(--muted)] font-mono text-[12px] text-center py-8">No messages yet.</p>
        ) : (
          messages.map((m) => <MessageBubble key={m.message_id} msg={m} />)
        )}
        <div ref={bottomRef} />
      </div>

      {/* Reply composer */}
      {ticket.status !== "spam" && ticket.status !== "closed" && (
        <div className="border-t border-[var(--hairline)] px-5 py-4 shrink-0 bg-[var(--paper)]" data-testid="reply-composer">
          <SectionLabel>Reply to {ticket.from_name}</SectionLabel>
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={`Type your reply…`}
            rows={4}
            data-testid="reply-textarea"
            className="w-full mt-2 p-3 rounded-[6px] border border-[var(--hairline)] bg-[var(--surface-2)] font-sans text-[13.5px] text-[var(--ink)] placeholder:text-[var(--muted)] outline-none focus:border-[var(--crimson)] resize-none transition-colors"
          />
          {sendError && (
            <p className="font-mono text-[11px] text-[var(--crimson)] mt-1.5" data-testid="reply-error">{sendError}</p>
          )}
          {sendOk && (
            <p className="font-mono text-[11px] text-emerald-600 mt-1.5" data-testid="reply-success">Reply sent successfully.</p>
          )}
          <div className="flex items-center justify-between mt-3">
            <span className="font-mono text-[10px] text-[var(--muted)]">
              Sends from {process.env.REACT_APP_SUPPORT_EMAIL || "support@support.dailynewshighlights.com"}
            </span>
            <button
              onClick={handleSend}
              disabled={sending || !replyText.trim()}
              data-testid="reply-send-btn"
              className="inline-flex items-center gap-2 h-8 px-4 rounded-full bg-[var(--ink)] text-[var(--paper)] font-mono text-[11px] uppercase tracking-[0.1em] hover:bg-[var(--crimson)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
              {sending ? "Sending…" : "Send Reply"}
            </button>
          </div>
        </div>
      )}

      {/* Closed/Spam — re-open hint */}
      {(ticket.status === "closed" || ticket.status === "spam") && (
        <div className="border-t border-[var(--hairline)] px-5 py-3 bg-[var(--surface-2)] text-center shrink-0">
          <span className="font-mono text-[11px] text-[var(--muted)]">
            Ticket is {ticket.status}. &nbsp;
            <button
              onClick={() => handleStatusChange("open")}
              className="text-[var(--crimson)] hover:underline"
              data-testid="ticket-reopen-hint"
            >
              Re-open to reply
            </button>
          </span>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════
// Main SupportDesk component
// ══════════════════════════════════════════════════════════════

export default function SupportDesk() {
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const loadStats = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/api/admin/support/stats`, { credentials: "include" });
      if (r.ok) setStats(await r.json());
    } catch {}
  }, []);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (catFilter !== "all") params.set("category", catFilter);
      if (debouncedQ) params.set("q", debouncedQ);
      const r = await fetch(`${BASE}/api/admin/support/tickets?${params}`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        setTickets(data.tickets || []);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, catFilter, debouncedQ]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadTickets(); }, [loadTickets]);

  const handleSelectTicket = (id) => {
    setSelectedId(id);
    setShowDetail(true);
  };

  const STATUS_FILTERS = [
    { key: "all",         label: "All" },
    { key: "open",        label: "Open" },
    { key: "in_progress", label: "In Progress" },
    { key: "closed",      label: "Closed" },
    { key: "spam",        label: "Spam" },
  ];

  const CAT_FILTERS = [
    { key: "all",       label: "All Categories" },
    { key: "contact",   label: "Contact" },
    { key: "support",   label: "Support" },
    { key: "complaint", label: "Complaint" },
    { key: "refund",    label: "Refund" },
    { key: "general",   label: "General" },
  ];

  return (
    <section className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 py-8" data-testid="support-desk">
      {/* Stats */}
      <StatsRow stats={stats} />

      {/* Inbound webhook info banner */}
      <div className="mb-5 flex items-center gap-3 px-4 py-3 rounded-[6px] border border-[var(--hairline)] bg-[var(--surface-2)]">
        <Mail size={14} className="text-[var(--crimson)] shrink-0" />
        <div>
          <span className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[var(--muted)]">Inbound address</span>
          <span className="ml-2 font-mono text-[11.5px] text-[var(--ink)] font-bold">support@support.dailynewshighlights.com</span>
        </div>
        <span className="ml-auto font-mono text-[10px] text-[var(--muted)] hidden sm:block">
          Webhook: /api/webhook/email-inbound
        </span>
      </div>

      {/* Main layout — split panel */}
      <div className="border border-[var(--hairline)] rounded-[8px] overflow-hidden flex" style={{ height: "calc(100vh - 380px)", minHeight: 480 }}>

        {/* LEFT — Ticket list */}
        <div
          className={`border-r border-[var(--hairline)] flex flex-col ${showDetail ? "hidden md:flex" : "flex"} w-full md:w-[360px] lg:w-[400px] shrink-0`}
          data-testid="ticket-list-panel"
        >
          {/* Search + filters */}
          <div className="border-b border-[var(--hairline)] px-4 py-3 space-y-2.5 shrink-0">
            <div className="flex items-center gap-2 h-8 px-3 rounded-full border border-[var(--hairline)] bg-[var(--surface-2)]">
              <Search size={12} className="text-[var(--muted)]" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search tickets…"
                data-testid="support-search-input"
                className="bg-transparent outline-none font-mono text-[11.5px] text-[var(--ink)] placeholder:text-[var(--muted)] flex-1"
              />
              {q && <button onClick={() => setQ("")}><X size={11} className="text-[var(--muted)]" /></button>}
            </div>
            {/* Status filters */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
              {STATUS_FILTERS.map((f) => (
                <FilterChip
                  key={f.key}
                  active={statusFilter === f.key}
                  onClick={() => setStatusFilter(f.key)}
                  testId={`status-filter-${f.key}`}
                >
                  {f.label}
                  {f.key !== "all" && stats?.[f.key] ? ` · ${stats[f.key]}` : ""}
                </FilterChip>
              ))}
            </div>
            {/* Category filters */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
              {CAT_FILTERS.map((f) => (
                <FilterChip
                  key={f.key}
                  active={catFilter === f.key}
                  onClick={() => setCatFilter(f.key)}
                  testId={`cat-filter-${f.key}`}
                >
                  {f.label}
                </FilterChip>
              ))}
            </div>
          </div>

          {/* Ticket count */}
          <div className="px-4 py-2 border-b border-[var(--hairline)] bg-[var(--surface-2)] shrink-0 flex items-center justify-between">
            <span className="font-mono text-[10.5px] text-[var(--muted)] uppercase tracking-[0.1em]">
              {loading ? "Loading…" : `${total} ticket${total !== 1 ? "s" : ""}`}
            </span>
            <button
              onClick={() => { loadTickets(); loadStats(); }}
              className="text-[var(--muted)] hover:text-[var(--crimson)] transition-colors"
              data-testid="tickets-refresh-btn"
            >
              <RefreshCw size={12} />
            </button>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto" data-testid="tickets-list">
            {!loading && tickets.length === 0 ? (
              <div className="py-16 text-center text-[var(--muted)] font-mono text-[12px]" data-testid="tickets-empty">
                <Inbox size={28} className="mx-auto mb-3 opacity-30" />
                <p>No tickets found.</p>
                {(statusFilter !== "all" || catFilter !== "all" || q) && (
                  <button
                    onClick={() => { setStatusFilter("all"); setCatFilter("all"); setQ(""); }}
                    className="mt-2 text-[var(--crimson)] hover:underline text-[11px]"
                    data-testid="clear-filters-btn"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              tickets.map((t) => (
                <TicketRow
                  key={t.ticket_id}
                  ticket={t}
                  isSelected={selectedId === t.ticket_id}
                  onClick={() => handleSelectTicket(t.ticket_id)}
                />
              ))
            )}
          </div>
        </div>

        {/* RIGHT — Ticket detail */}
        <div
          className={`flex-1 flex flex-col ${!showDetail ? "hidden md:flex" : "flex"}`}
          data-testid="ticket-detail-container"
        >
          {selectedId ? (
            <TicketDetail
              ticketId={selectedId}
              onBack={() => setShowDetail(false)}
              onRefreshList={() => { loadTickets(); loadStats(); }}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-8" data-testid="no-ticket-selected">
              <Inbox size={36} className="text-[var(--muted)] opacity-30 mb-4" />
              <p className="font-serif text-[16px] text-[var(--ink)] mb-1">Select a ticket</p>
              <p className="font-mono text-[11.5px] text-[var(--muted)]">
                Choose a ticket from the list to view the thread and reply.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
